import { NoInputTimeoutOverride } from "@app/form_components";
import { RichTextReplyButtonsForm } from "@app/form_features/reply-buttons";
import { ObjectDropdown } from "@app/page-components";
import {
    ConfigViewType,
    DesignChannelType,
    ObjectClass,
    NodeTypeName
} from "@app/types";
import { configRemoveNullish } from "@app/utilities/parse/parseNode";
import BaseNodeModel from "../BaseNodeModel";

export default class RichTextNodeModel extends BaseNodeModel {
    /**
     * Rich text node model
     * @module RichTextNodeModel
     * @param {string} nodeName Node display name
     * @param {number} x Node x coordinate on canvas
     * @param {number} y Node y coordinate on canvas
     * @param {Object} props Pre-defined node properties; if non-null, apply values to node properties
     * @param {string} id If non-null, use this ID as the node ID
     */
    constructor(nodeName = "UNTITLED_NODE", x = 0, y = 0, props = null, id = null) {
        super(nodeName, x, y, null, id);
        this.props.nodeTypeName = NodeTypeName.richText;
        this.props.colorClass = "yellow";
        this.props.iconClass = "codepen";
        this.DEFAULT_COLOR_CLASS = "yellow";
        this.applyProps(props);
        this.props.config.dataType = "INTENT";
    }

    static clearRemovableFields(instance) {
        if (instance?.props?.config) {
            instance.props.config = configRemoveNullish(instance.props.config, ["maxIntentTimeout", "noInputTimeout"]);
        }
    }

    /** @inheritdoc */
    setConfig(key, value) {

        if (key === "type") {
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        }

        if ((key === "maxIntentTimeout" || key === "noInputTimeout") && value !== "") {
            value = Number(value);
        }

        super.setConfig(key, value);

        RichTextNodeModel.clearRemovableFields(this);
    }

    /** @inheritdoc */
    getConfigViews() {
        const {
            disambiguationPromptId,
            noInputTimeoutOverride
        } = this.props.config;

        /** @type {import("../../types").ConfigView[]} */
        const configView = [
            { type: "Switch", key: "sensitiveData", label: this.getPropLabel("sensitive_data"), checked: false },
            { type: "Input", label: this.getPropLabel("maxIntentTimeout"), key: "maxIntentTimeout", hint: this.getPropLabel("time_in_milliseconds"), tooltip: "The total length of an utterance that can possibly be given. Defaulted to 50000" },
            (
                <div className="form-component-container">
                    <ObjectDropdown
                        isClearable
                        label={this.getPropLabel("disambiguationPromptId")}
                        name="disambiguationPromptId"
                        onChange={(newValue) => {
                            this.setConfigAndParent('disambiguationPromptId', newValue);
                        }}
                        tooltip={this.getPropLabel("disambiguationPromptIdTooltip")}
                        type={ObjectClass.prompt}
                        useStateValue
                        value={disambiguationPromptId}
                    />
                </div>
            ),
            {
                content: (
                    <NoInputTimeoutOverride
                        onValueChange={(newValue) => {
                            this.setConfigAndParent('noInputTimeoutOverride', newValue);
                        }}
                        value={noInputTimeoutOverride}
                        excludeChannels={[DesignChannelType.voice]}
                    />
                ),
                label: this.getPropLabel("noInputTimeout"),
                tooltip: this.getPropLabel("noInputTimeout_tooltip"),
                type: ConfigViewType.jsx,
            }
        ];
        
        return [
            ...this.getConfigViewNameField(),
            ...[
                { type: "Textarea", label: this.getPropLabel("description"), key: "description", value: "Rich Text Node", rows: 3},
                { type: "hr" },
                <RichTextReplyButtonsForm
                    name="reply-buttons"
                    onChange={(newButtons) => {
                        this.setConfig('buttons', newButtons);
                        this.saveChanges('buttons', newButtons);
                    }}
                    value={this.getConfig('buttons') || []}
                />,
                ...configView,
                { type: "hr" },
                { type: "Textarea", label: this.getPropLabel("note"), key: "note" }
            ]
        ];
    }
}